import React, { useContext, useRef } from 'react';
import { QueryContext, QueryProvider } from '@thd-nucleus/data-sources';
import { CollectionInfoTabsDesktop } from '@thd-olt-component-react/collection-info-tabs';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Col, Row } from '@thd-olt-component-react/grid';
import { Heading } from '@thd-olt-component-react/heading';
import { MediaGalleryDesktop } from '@thd-olt-component-react/media-gallery';
import { Rating } from '@thd-olt-component-react/rating';
import { SalientListItem } from '@thd-olt-component-react/salient-points';
import { ListViewPod } from './ListViewPod';
import { PriceRange } from '../price-range/PriceRange';
import { collectionQueryOption, getCollectionRatingsStats } from '../CollectionHelpers';
import { ListViewPropTypes } from './ListViewPropTypes';
import './list-view.scss';

export const ListViewCollectionDesktop = ({ collectionId, collection, storeId, skipFn }) => {
  const { defaultVariables } = useContext(QueryContext);

  const optionsRef = useRef();
  const { details, media, metadata, products } = collection;
  const {
    brandName, canonicalUrl, name, productCount
  } = metadata || {};
  const { allProducts } = productCount || {};
  const { highlights } = details || {};

  let collectionRatingStats = getCollectionRatingsStats({ products });

  let collectionRating = collectionRatingStats.collectionRatingReviewCount === 0
    ? 0
    : collectionRatingStats.collectionRating / collectionRatingStats.collectionRatingReviewCount;

  const scrollToOptions = () => {
    if (optionsRef.current) {
      optionsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const title = <span>Shop the Collection <span className="collections__count">({allProducts})</span></span>;

  return (
    <>
      {media && (
        <Col xs="6">
          <ErrorBoundary name="collection-media-gallery">
            <MediaGalleryDesktop collectionId={collectionId} />
          </ErrorBoundary>
        </Col>
      )}
      <Col xs={media ? 6 : 12}>
        <ErrorBoundary name="collection-details">
          <h2 className="product-header__brand product-header__brand--medium">
            <a href={canonicalUrl}>{brandName}</a>
          </h2>
          <h1 className="product-header__collection-name product-header__collection-name--xlarge">{name}</h1>

          <Row>
            <div className="collections_details-ratings">
              <Rating className="listViewReviews" value={collectionRating} />
              <span>({collectionRatingStats.collectionRatingReviewCount})</span>
            </div>
          </Row>
          <ErrorBoundary className="collections__product-range">
            <div className="price__wrapper">
              <PriceRange products={products} numberOfProduct={allProducts} />
            </div>
          </ErrorBoundary>
          {(highlights || []).map((highlight, highlightIndex) => {
            return (<SalientListItem key={highlightIndex}>{highlight}</SalientListItem>);
          })}
          <div className="collections__choose-options">
            <button
              type="button"
              className="bttn-outline bttn-outline--dark"
              onClick={() => scrollToOptions()}
            >
              <span className="bttn__content">Choose Your Options Below</span>
            </button>
          </div>
        </ErrorBoundary>
      </Col>
      <Col>
        <ErrorBoundary name="collection-info">
          <CollectionInfoTabsDesktop collectionId={collectionId} />
        </ErrorBoundary>
      </Col>
      <Col className="list-view__collection">
        <ErrorBoundary name="collection-shop">
          <div ref={optionsRef} />
          <Heading className="list-view__heading" underline>
            {title}
          </Heading>
          <QueryProvider
            dataSource="Collection"
            skip={skipFn}
            defaultVariables={{
              skipSubscribeAndSave: true,
              skipInstallServices: true,
              skipSpecificationGroup: false,
              storeId,
              skipKPF: true,
              ...(typeof defaultVariables?.current?.isBrandPricingPolicyCompliant === 'boolean'
              && { isBrandPricingPolicyCompliant:
                defaultVariables.current.isBrandPricingPolicyCompliant })
            }}
            queryOptions={collectionQueryOption}
            cacheKey="listview-desktop"
          >
            {(products || []).map((product, productIndex) => {
              const itemId = product.identifiers?.itemId;
              return (
                <ErrorBoundary name={`collection-info-pod-${productIndex}`} key={productIndex}>
                  <ListViewPod
                    itemId={itemId}
                    position={productIndex}
                    parent="collection"
                    storeId={storeId}
                  />
                </ErrorBoundary>
              );
            })}
          </QueryProvider>
        </ErrorBoundary>
      </Col>
    </>
  );
};

ListViewCollectionDesktop.displayName = 'ListViewCollectionDesktop';

ListViewCollectionDesktop.propTypes = ListViewPropTypes;